/**
 * DuerpListPage - Liste des DUERP de l'utilisateur
 * Permet de créer, ouvrir et gérer ses documents DUERP
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardBody } from '../components/ui/Card'
import EmptyState from '../components/ui/EmptyState'
import { LegalDisclaimer } from '../components/legal'
import { duerpAPI, DuerpDocument, SECTEUR_OPTIONS } from '../services/duerp'
import { useAuthStore } from '../stores/authStore'

export default function DuerpListPage() {
  const navigate = useNavigate()
  const { isAuthenticated, openLoginModal } = useAuthStore()
  const [documents, setDocuments] = useState<DuerpDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Charger les DUERP
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false)
      return
    }

    const loadDocuments = async () => {
      try {
        setIsLoading(true)
        const result = await duerpAPI.list()
        setDocuments(result.documents)
      } catch (err) {
        // Extraction robuste du message d'erreur
        setError(
          err instanceof Error
            ? err.message
            : typeof err === 'string'
            ? err
            : 'Erreur de chargement'
        )
      } finally {
        setIsLoading(false)
      }
    }

    loadDocuments()
  }, [isAuthenticated])

  // Si non authentifié
  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 text-center">
        <Card>
          <CardBody className="py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Module DUERP
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Structurez votre Document Unique d'Évaluation des Risques
              Professionnels pour le risque chimique.
            </p>
            <button onClick={openLoginModal} className="btn-primary">
              Se connecter pour accéder
            </button>
          </CardBody>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes DUERP</h1>
          <p className="text-sm text-gray-600 mt-1">
            Aide à la structuration du Document Unique — Risque Chimique
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouveau DUERP
        </button>
      </div>

      {/* Avertissement */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Module DUERP V1 — Usage professionnel individuel</p>
            <p className="mt-1 text-amber-700">
              Outil d'aide à la structuration destiné à des professionnels autonomes.
              RISK-DATA est un agrégateur de données ; il n'effectue aucune évaluation
              réglementaire. L'employeur reste seul responsable du document final
              (articles L4121-1 et R4121-1 du Code du travail).
            </p>
          </div>
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      ) : error ? (
        <Card className="border-red-200 bg-red-50">
          <CardBody className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-sm text-red-700 underline"
            >
              Réessayer
            </button>
          </CardBody>
        </Card>
      ) : documents.length === 0 ? (
        <EmptyState
          icon={
            <svg
              className="w-full h-full"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="Aucun DUERP"
          description="Créez votre premier Document Unique pour structurer l'évaluation des risques chimiques de votre établissement."
          action={{
            label: 'Créer un DUERP',
            onClick: () => setShowCreateModal(true),
          }}
        />
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <DuerpCard key={doc.id} document={doc} />
          ))}
        </div>
      )}

      {/* Modal création */}
      {showCreateModal && (
        <CreateDuerpModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(doc) => {
            setShowCreateModal(false)
            navigate(`/duerp/${doc.id}`)
          }}
        />
      )}
    </div>
  )
}

/**
 * Carte DUERP
 */
function DuerpCard({ document }: { document: DuerpDocument }) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <Link to={`/duerp/${document.id}`}>
      <Card className="hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
        <CardBody className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">
              {document.raison_sociale}
            </h3>
            <p className="text-sm text-gray-500 truncate mt-1">
              {document.adresse}
              {document.ville && ` - ${document.ville}`}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span>Modifié le {formatDate(document.updated_at)}</span>
              <span>{document.nb_unites || 0} unité(s)</span>
              <span>{document.nb_substances || 0} substance(s)</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
            {document.secteur_activite && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                {SECTEUR_OPTIONS.find(s => s.value === document.secteur_activite)?.label || document.secteur_activite}
              </span>
            )}
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </CardBody>
      </Card>
    </Link>
  )
}

/**
 * Modal de création DUERP
 */
function CreateDuerpModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (doc: DuerpDocument) => void
}) {
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    raison_sociale: '',
    adresse: '',
    code_postal: '',
    ville: '',
    siret: '',
    effectif_total: '',
    secteur_activite: '',
    responsable_nom: '',
    responsable_fonction: '',
  })

  // Fermer et réinitialiser
  const handleClose = () => {
    setStep(1)
    onClose()
  }

  // Valider étape 1 et passer à l'étape 2
  const goToStep2 = () => {
    if (!formData.raison_sociale.trim()) {
      setError('La raison sociale est requise')
      return
    }
    setError(null)
    setStep(2)
  }

  // Retour à l'étape 1
  const goToStep1 = () => {
    setError(null)
    setStep(1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.raison_sociale.trim() || !formData.adresse.trim()) {
      setError('Raison sociale et adresse sont requis')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const result = await duerpAPI.create({
        raison_sociale: formData.raison_sociale.trim(),
        adresse: formData.adresse.trim(),
        code_postal: formData.code_postal.trim() || undefined,
        ville: formData.ville.trim() || undefined,
        siret: formData.siret.trim() || undefined,
        effectif_total: formData.effectif_total ? parseInt(formData.effectif_total) : undefined,
        secteur_activite: formData.secteur_activite || undefined,
        responsable_nom: formData.responsable_nom.trim() || undefined,
        responsable_fonction: formData.responsable_fonction.trim() || undefined,
      })

      onCreate(result.document)
    } catch (err) {
      // Extraction robuste du message d'erreur
      if (err instanceof Error) {
        setError(err.message)
      } else if (typeof err === 'string') {
        setError(err)
      } else if (err && typeof err === 'object') {
        const errObj = err as Record<string, unknown>
        setError(
          typeof errObj.message === 'string'
            ? errObj.message
            : typeof errObj.error === 'string'
            ? errObj.error
            : 'Erreur lors de la création'
        )
      } else {
        setError('Erreur lors de la création')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Nouveau DUERP
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Étape {step}/2 — {step === 1 ? 'Identification' : 'Coordonnées'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Indicateur de progression */}
          <div className="flex gap-2 mt-4">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-blue-500' : 'bg-gray-200'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Étape 1: Identification */}
          {step === 1 && (
            <>
              {/* Disclaimer légal DUERP (CDC v1.2 §9.3) */}
              <LegalDisclaimer variant="duerp" className="mb-2" />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={formData.raison_sociale}
                  onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nom de l'entreprise"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SIRET
                </label>
                <input
                  type="text"
                  value={formData.siret}
                  onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="12345678901234"
                  maxLength={14}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secteur d'activité
                </label>
                <select
                  value={formData.secteur_activite}
                  onChange={(e) => setFormData({ ...formData, secteur_activite: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionner...</option>
                  {SECTEUR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Étape 2: Coordonnées */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Responsable DUERP
                  </label>
                  <input
                    type="text"
                    value={formData.responsable_nom}
                    onChange={(e) => setFormData({ ...formData, responsable_nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nom"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonction
                  </label>
                  <input
                    type="text"
                    value={formData.responsable_fonction}
                    onChange={(e) => setFormData({ ...formData, responsable_fonction: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Responsable HSE"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Adresse du site"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={formData.code_postal}
                    onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="75001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Paris"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effectif total
                </label>
                <input
                  type="number"
                  value={formData.effectif_total}
                  onChange={(e) => setFormData({ ...formData, effectif_total: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50"
                  min="1"
                />
              </div>
            </>
          )}

          {/* Boutons de navigation */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={goToStep2}
                  className="btn-primary"
                >
                  Continuer
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={goToStep1}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Retour
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Création...' : 'Créer le DUERP'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
